import { Component, inject, computed, OnInit } from '@angular/core';
import { Router } from '@angular/router'; 
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from "primeng/api";
import { UserContextService } from '../../services/safeguard/user-context.service';
import { SharedSignalService } from '../../services/config/shared-signal.service';
import { CustomLoggerService } from '../../services/monitor/custom-logger.service';
import { NgRecaptcha3Service } from 'ng-recaptcha3';
import { AuthService } from 'src/app/services/safeguard/auth.service';

export const StrongPasswordRegx: RegExp =
  /^(?=[^A-Z]*[A-Z])(?=[^a-z]*[a-z])(?=\D*\d).{8,}$/;

@Component({
  selector: 'app-password-recovery',
  standalone: false,
  templateUrl: './password-recovery.component.html',
  styleUrl: './password-recovery.component.css',
  // imports: [CommonModule, ReactiveFormsModule]
})

export class PasswordRecoveryComponent implements OnInit {

  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);   
  private readonly userContextService = inject(UserContextService);
  private readonly authService = inject(AuthService);
  private readonly sharedSignalService = inject(SharedSignalService);
  private readonly customLoggerService = inject(CustomLoggerService);

  captchaToken: string | null = null;

  recovPhase = computed(() => this.sharedSignalService.getPasswordRecoveryPhase());
  phase!: string;
  subHeader!: string

  userName: string = '';
  userNameRecovered: boolean = false;

  errorMessage!: string;
  isErrorMessageShown: boolean = false;

  public requestPasswordResetForm!: FormGroup;
  public userNameRecoveryForm!: FormGroup;
  public passwordResetForm!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly recaptcha3: NgRecaptcha3Service
    // private recaptchaV3Service: ReCaptchaV3Service
  ) {}

  ngOnInit() {
    this.requestPasswordResetForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.email, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$")]],
      userName: ['', Validators.required]
    });

    this.userNameRecoveryForm = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.email, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$")]]
    });


    this.phase = this.recovPhase();
    switch(this.phase) {
      // case 'pswd-reset-request':
      //   this.subHeader = 'Enter your credentials for receiving a link to reset the password:'
      //   break;
      case 'user-name-recov':
        this.subHeader = 'Enter your email address to receive the forgotten username:'
        break;
      case 'password-reset':
        this.subHeader = 'Enter temporary and new passwords:'
        break;
    }

    // Initializa the reCapture service based on the site key
    this.authService.getCaptchaSiteKey()
      .subscribe({
        next: (response) => {
          let captchaSiteKey = response.message;
          this.recaptcha3.init(captchaSiteKey);
        },
        error: (error) => {
          this.displayMessage("Error", error.message || 'System error.  Please see system administrator.');
        },
        complete: () => {
        }
      });

  }

  // Change the view of the form if the user forgot his/her username
  onForgotUserName() {
    this.sharedSignalService.setPasswordRecoveryPhase('user-name-recov');
    this.phase = 'user-name-recov';
    this.subHeader = 'Enter your email address to receive the forgotten username:'
  }

  // Submit username to receive an email with a link to reset the password
  onRequestPasswordReset() {
    if (this.requestPasswordResetForm.invalid) {
      return;
    }

    // Generate new Captcha token
    this.recaptcha3.getToken({action: 'password_recovery'}).then(captchaToken => {
      // Submit the request to the endpoint by calling the service class
      const userName: string = this.requestPasswordResetForm.get('userName')?.value;
      const emailAddress: string = this.requestPasswordResetForm.get('emailAddress')?.value;
      this.userContextService.requestPasswordReset(userName, emailAddress, captchaToken)
        .subscribe({
          next: (response) => {
            console.log('response', response);
            switch (response.status) {
              case 'FAILURE':
              case 'BAD_REQUEST':
                this.displayMessage("Error", response.returnMessage || 'System error.  Please see system administrator.');
                break;
              case 'ERROR':
                this.displayMessage("Error", response.returnMessage || 'Sorry, looks like some kind of a system error happened.  Please try again.');
                break;
              case 'SUCCESS':
                this.displayMessage('Processed', 'An email with your username has been sent to ' + emailAddress);
                break;
            }
          },
          error: (error) => {
            this.displayMessage("Error", error.message || 'System error.  Please see system administrator.');
          },
          complete: () => {
          }
        });
    }, error => {
      this.customLoggerService.logWarn('passwword-recovery.onRequestPasswordReset()', 'PR-1', error.message, 'PSWD-RESET');
    });
  }

  // Submit email address to receive an email with a recovered username
  onRecoverUserName(form: any) {
    if (this.userNameRecoveryForm.invalid) {
      return;
    }

    // Generate new Captcha token
    this.recaptcha3.getToken({action: 'password_recovery'}).then(captchaToken => {
      // Submit the request to the endpoint by calling the service class
      const emailAddress: string = this.userNameRecoveryForm.get('emailAddress')?.value;
      this.userContextService.requestUserName(emailAddress, captchaToken)
        .subscribe({
          next: (response) => {
            console.log('response', response);
            // const resp = Object.entries(response);
            switch (response.status) {
              case 'BAD_REQUEST':
                this.displayMessage("Error", response.returnMessage || 'System error.  Please see system administrator.');
                break;
              case 'ERROR':
                this.displayMessage("Error", response.returnMessage || 'Sorry, looks like some kind of a system error happened.  Please try again.');
                break;
              case 'FAILURE':
                if (response.additionalInfo && response.additionalInfo.size > 0) {
                  switch(response.additionalInfo?.get('uvStatus')) {
                    case 'EMAIL_ADDRESS_NOT_FOUND':
                      this.displayMessage('Warning', 'The entered email address is not found, please try again');
                      break;
                  }
                } else {
                  this.displayMessage("Error", response.returnMessage || 'Sorry, looks like some kind of a system error happened.  Please try again.');
                }
                this.isErrorMessageShown = true;
                break;
              case 'SUCCESS':
                this.displayMessage('Processed', 'An email with your username has been sent to ' + emailAddress);
                this.sharedSignalService.setPasswordRecoveryPhase('pswd-reset-request');
                // this.phase = 'pswd-reset-request';
                // this.subHeader = 'Enter your username to receive a temporary password:';
                break;
            }
          },
          error: (error) => {
            console.log('error', error);
            this.displayMessage("Error", error.message || 'System error.  Please see system administrator.');
          },
          complete: () => {
          }
        });

    }, error => {
      this.customLoggerService.logWarn('passwword-recovery.onRecoverUserName()', 'PR-2', error.message, 'PSWD-RESET');
    });

  }

  // // Submit a request with the temp and new passwords
  // onSubmitPasssword() {
  //   const { tempPassword, newPassword, confirmPassword } = this.passwordResetForm.value;
  //   if (newPassword != confirmPassword) {
  //     this.displayMessage("New and confirm passwords do not match.");
  //     return;
  //   }
  //   else if (newPassword === tempPassword) {
  //     this.displayMessage("New and temporary passwords must be different.");
  //     return;
  //   }
  //   else {
  //     this.userContextService.changeUserPassword(this.userName, tempPassword, newPassword)
  //     .subscribe({
  //       next: (response) => {
  //         this.router.navigate(['/login']);
  //         this.displayMessage("The user's password has been successfully changed.");
  //         return;                      
  //       },
  //       error: (error) => {
  //         this.displayMessage('Password must contain at least one digit, lowercase, uppercase and special characters with the length of 8 to 20 characters.');
  //       },
  //       complete: () => {
  //       }
  //     });
  //   }


  //   this.requestPasswordResetForm.reset();
  //   this.userNameRecoveryForm.reset();
  //   this.passwordResetForm.reset();
  // }

  onCancel() {
      this.sharedSignalService.setPasswordRecoveryPhase('');
      this.router.navigate(['/login']);
  }

  ngOnDestroy() {
    this.recaptcha3.destroy();
  }

  displayMessage(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      key: 'password-reset',      
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info  p-button-rounded",
      accept: () => {
            return;
          }
    });
  }

  // onCaptchaSuccess(token: string): void {
  //   this.captchaPassed = true;
  //   this.captchaToken = token;
  //   console.log('CAPTCHA success:', token);
  //   if (this.captchaPassed && this.captchaToken) {
  //     // Send token to backend for verification
  //     console.log('Form submitted with CAPTCHA token:', this.captchaToken);
  //     this.userContextService.verifyCatcha(this.captchaToken)
  //     .subscribe({
  //       next: (response) => {
  //         this.isCaptchaVerified = true;
  //         return;                      
  //       },
  //       error: (error) => {
  //         this.isCaptchaVerified = false;
  //         console.log('Captcha error', error);
  //         this.displayMessage("Error in verifying reCaptcha.");
  //       },
  //       complete: () => {
  //       }
  //     });

  //   }

  // }

  // onCaptchaError(error: any): void {
  //   this.captchaPassed = false;
  //   console.error('CAPTCHA error:', error);
  // }

  // onCaptchaExpire(): void {
  //   this.captchaPassed = false;
  //   this.captchaToken = null;
  //   this.isCaptchaVerified = false;
  //   console.warn('CAPTCHA expired');
  // }

  // onCaptchaSubmit(): void {
  //   if (this.captchaPassed && this.captchaToken) {
  //     // Send token to backend for verification
  //     console.log('Form submitted with CAPTCHA token:', this.captchaToken);
  //     this.userContextService.verifyCatcha(this.captchaToken)
  //     .subscribe({
  //       next: (response) => {
  //         this.isCaptchaVerified = true;
  //         return;                      
  //       },
  //       error: (error) => {
  //         this.isCaptchaVerified = false;
  //         console.log('Captcha error', error);
  //         this.displayMessage("Error in verifying reCaptcha.");
  //       },
  //       complete: () => {
  //       }
  //     });

  //   }
  // }


}
